import { useMemo } from "react";
import { markdownAHtml } from "@bilbobot/shared/markdown";
import { informeParaElCliente } from "@bilbobot/shared/informe-cliente";

/**
 * El informe del mes, como documento y listo para imprimir.
 *
 * ── Por qué no reescribe el informe, lo pinta ───────────────────────────
 *
 * El texto sale de `informeParaElCliente()`, que es el MISMO que se pega en un
 * correo. Si esto lo redactara por su cuenta, el PDF y el correo dirían cosas
 * distintas del mismo mes — y la que se acabaría mandando sería la que estuviera
 * más a mano, no la buena.
 *
 * Aquí sólo se le pone forma: portada, tipografía y saltos de página.
 *
 * ── Y el PDF sale de imprimir ───────────────────────────────────────────
 *
 * `Ctrl+P` → «Guardar como PDF». No es un apaño por no tener otra cosa: una
 * hoja de estilo de impresión bien hecha da el mismo resultado que generar el
 * PDF en el servidor, y el servidor que lo generaría es el que está sirviendo
 * las sesenta y siete webs de los clientes. Meterle Chromium para esto son
 * cuatrocientos megas, un proceso más que se cae y una cosa más que actualizar.
 *
 * Los tres gráficos del panel son SVG, así que salen nítidos en el papel. Si
 * algún día se hacen con `canvas`, saldrán en blanco: está comprobado y por eso
 * queda escrito.
 */

interface Props {
  cliente: string;
  mes: string;
  cuerpo: Record<string, unknown>;
  redaccion?: {
    lectura?: string;
    estrategia?: string;
    plan?: string;
  };
  cerrar: () => void;
}

/** Agosto de 2026, no «2026-08». En un documento que ve un cliente importa. */
const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export function mesEnLetra(mes: string): string {
  const [anio, num] = mes.split("-");
  const n = Number(num) - 1;
  return MESES[n] ? `${MESES[n]} de ${anio}` : mes;
}

export function InformeImprimible({
  cliente,
  mes,
  cuerpo,
  redaccion,
  cerrar,
}: Props) {
  const html = useMemo(() => {
    /*
     * Lo escrito a mano MANDA sobre lo generado.
     *
     * Y sólo sobre el texto: la lectura del mes, la estrategia y el plan. Las
     * visitas, los contactos y las posiciones salen de `cuerpo`, que está
     * congelado, y no hay ninguna forma de escribir encima de ellas desde aquí.
     * Es lo que permite que el informe se pueda editar sin poder falsearlo.
     */
    const conLoEscrito = {
      ...(cuerpo as Record<string, never>),
      cliente,
      mes,
      ...(redaccion?.lectura?.trim() ? { lectura: redaccion.lectura } : {}),
      ...(redaccion?.estrategia?.trim()
        ? { estrategia: redaccion.estrategia }
        : {}),
    };

    let texto = informeParaElCliente(conLoEscrito);

    /*
     * El plan reescrito sustituye a la lista generada, no se suma a ella.
     *
     * Si se sumaran, el cliente leería dos veces lo que haremos el mes que
     * viene y con dos redacciones distintas — que es peor que cualquiera de las
     * dos por separado.
     */
    if (redaccion?.plan?.trim()) {
      const corte = texto.indexOf("## Qué haremos el mes que viene");
      const antes = corte >= 0 ? texto.slice(0, corte) : `${texto}\n\n`;
      texto = `${antes}## Qué haremos el mes que viene\n\n${redaccion.plan.trim()}\n`;
    }

    /*
     * Se quita el «# Cómo ha ido agosto de 2026» del markdown.
     *
     * La portada ya lo dice, con el nombre del cliente encima, y repetirlo dos
     * veces seguidas queda a borrador. Se quita AQUÍ y no en el texto porque
     * ese mismo texto es el que se pega en un correo, y allí sí hace falta un
     * título.
     */
    const sinTitulo = texto.replace(/^#\s+.*\n+/, "");

    return markdownAHtml(sinTitulo);
  }, [cuerpo, cliente, mes, redaccion]);

  return (
    <div className="documento-fondo" role="dialog" aria-label="Informe del mes">
      {/* Los mandos NO se imprimen: la hoja de estilo de impresión los oculta. */}
      <div className="documento-mandos">
        <button type="button" className="acc" onClick={() => window.print()}>
          Imprimir o guardar en PDF
        </button>
        <span className="pista">
          En el diálogo de impresión, elige «Guardar como PDF» como destino.
        </span>
        <button type="button" className="acc secundaria" onClick={cerrar}>
          Cerrar
        </button>
      </div>

      <article className="documento">
        <header className="documento-portada">
          <p className="documento-agencia">Bilbolink</p>
          <h1>{cliente}</h1>
          <p className="documento-mes">Informe de {mesEnLetra(mes)}</p>
        </header>

        <div
          className="documento-cuerpo"
          /*
           * `markdownAHtml` escapa TODO el HTML antes de transformar, así que
           * lo que llega aquí no puede traer etiquetas de fuera. Un informe
           * lleva texto escrito por personas y datos de webs de clientes: es
           * justo donde no se puede confiar en la entrada.
           */
          dangerouslySetInnerHTML={{ __html: html }}
        />

        <footer className="documento-pie">
          <p>
            Bilbolink · bilbolink.com · Informe de {mesEnLetra(mes)} para{" "}
            {cliente}
          </p>
        </footer>
      </article>
    </div>
  );
}
